import * as React from 'react';

import { MediaResourceTypes } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import cx from 'classnames';

import { MediaPlayer } from '~shared/components/MediaPlayer';

import ActivityImage from '../../../../shared/components/activity/ActivityImage';
import { Point } from '../../components/Point';
import { VideoErrorScreen } from './VideoErrorScreen';

interface QuestionFeedbackViewProps {
  answers: any[];
  correctAnswerIds: number[];
  textAnswers: string[];
  gradingWeight: number;
  explanation: string;
  image: any;
  isCorrect: boolean;
  okClicked: () => void;
  questionText: string;
  questionMedia: any;
  showGradingWeight: boolean;
  questionId: number;
}

const QuestionFeedbackView = (props: QuestionFeedbackViewProps) => {
  const {
    answers,
    correctAnswerIds,
    textAnswers,
    gradingWeight,
    explanation,
    image: explanationImage,
    isCorrect,
    okClicked,
    questionText,
    questionMedia,
    showGradingWeight,
    questionId,
  } = props;

  const containerClass = cx('modal-dialog', {
    'feedback-container-nocorrect': isCorrect === null,
    'feedback-container-correct': isCorrect === true,
    'feedback-container-incorrect': isCorrect === false,
  });

  let correctAnswers = [];

  if (textAnswers && textAnswers.length > 0) {
    correctAnswers = textAnswers.map((text) => ({ text: text }));
  } else if (correctAnswerIds) {
    correctAnswerIds.forEach((correctAnswerId) => {
      const matchedAnswer = answers.find(
        (answer) => answer.id == correctAnswerId
      );
      if (matchedAnswer) correctAnswers.push(matchedAnswer);
    });
  }

  return (
    <div id="feedback-popup" className="modal open">
      <div id="feedback-container" className={containerClass}>
        <div className="modal-header">
          {(() => {
            if (isCorrect === null) {
              return (
                <div
                  key="no-correctness-title-bar"
                  className="no-correctness-title-bar"
                >
                  <h4>{translate('Submitted')}</h4>
                </div>
              );
            } else if (isCorrect) {
              return (
                <div key="correct-title-bar" className="correct-title-bar">
                  <h4>
                    <i data-icon="d" /> {translate('Correct!')}
                  </h4>
                </div>
              );
            } else {
              return (
                <div key="incorrect-title-bar" className="incorrect-title-bar">
                  <h4>
                    <i data-icon="h" /> {translate('Incorrect')}
                  </h4>
                </div>
              );
            }
          })()}
        </div>
        <div className="modal-body">
          <span key="question-text" id="question-text">
            <b>{translate('Question:')}</b>
            <pre dangerouslySetInnerHTML={{ __html: questionText }} />
          </span>

          {questionMedia?.type === MediaResourceTypes.Image ? (
            <div
              className="student-quiz-image-container"
              key={`student-quiz-image-container-${questionMedia?.url}`}
              style={{ marginBottom: 11 }}
            >
              <ActivityImage imageUrl={questionMedia?.url} />
            </div>
          ) : null}

          {questionMedia?.type === MediaResourceTypes.Video ? (
            <MediaPlayer
              key={`question-feedback-video-${questionId}`}
              url={questionMedia?.url}
              start={questionMedia?.start}
              end={questionMedia?.end}
              type={MediaResourceTypes.Video}
              controls
              containerStyle="student-question-media-feedback-container"
              playerStyle="activity-media-container"
              ErrorScreen={VideoErrorScreen}
            />
          ) : null}

          {showGradingWeight ? (
            <div>
              <Point gradingWeight={gradingWeight} />
            </div>
          ) : null}

          {correctAnswers && correctAnswers.length > 0 ? (
            <>
              <div key="answer-header" className="answer-header">
                {correctAnswers.length === 1
                  ? translate('Correct Answer') + ':'
                  : translate('Correct Answers')}
              </div>
              <div key="answer-text" id="answer-text">
                {correctAnswers.map((answer) => (
                  <div
                    key={`${answer.question_id}-${answer.order}`}
                    className="answer"
                  >
                    {answer.text && answer.text.length && (
                      <span
                        dangerouslySetInnerHTML={{ __html: answer.text }}
                      ></span>
                    )}
                    {answer.image && <img src={answer.image.urlXS}></img>}
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {explanation || explanationImage ? (
            <>
              <span key="explanation-text" id="explanation-text">
                <b>{translate('Explanation:')}</b>
                {explanation.length > 0 ? (
                  <pre dangerouslySetInnerHTML={{ __html: explanation }} />
                ) : null}
              </span>
              {explanationImage ? (
                <div
                  key="explanationImageWrapper"
                  className="explanationImageWrapper"
                >
                  <ActivityImage imageUrl={explanationImage.url} />
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div className="modal-footer">
          <button
            id="submit-feedback-button"
            className="button button-large button-primary"
            onClick={okClicked}
          >
            {translate('OK')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionFeedbackView;
